/**
 * Marketplace API Service
 * Designer and contractor browsing, search, profiles, assignments, invitations
 */

import type { Project } from "@/features/projects/services/projects-api.service";
import { apiClient } from "@/lib/api-client";
import { escrowShortfallOf } from "@/lib/errorTransformer";

const API_BASE = "/api/v1";

// ============================================================================
// TYPES
// ============================================================================

export interface PreviousProject {
	id?: string;
	title: string;
	description?: string;
	year?: number;
	location?: string;
	budgetMin?: number;
	budgetMax?: number;
	durationMonths?: number;
	projectType?: string;
	status?: string;
	imageUrls?: string[];
	sortOrder?: number;
}

export interface DesignerProfile {
	id: string;
	userId: string;
	name: string;
	firmName?: string | null;
	bio?: string | null;
	profilePictureUrl?: string | null;
	portfolioImages?: string[];
	previousProjects?: PreviousProject[];
	services?: string[];
	serviceCities?: string[];
	styles?: string[];
	priceRangeMin?: number | null;
	priceRangeMax?: number | null;
	experienceYears?: number;
	city?: string | null;
	state?: string | null;
	pincode?: string | null;
	/** DESIGNER_PROJECT_TYPES slugs. */
	projectTypes?: string[];
	projectTypesOther?: string | null;
	projectsCompleted?: number;
	isVerified: boolean;
	/** Present on `GET /designers` list items only; null when unreviewed. */
	averageRating?: number | null;
	reviewCount?: number;
	reviewStatus?: "pending" | "approved" | "rejected";
	reviewNotes?: string | null;
	/**
	 * Returned by `GET /designers/:id` only: the slug of the approved portfolio
	 * the directory page links to, or null when none is approved.
	 */
	portfolioSlug?: string | null;
}

/** The company, team and projects a contractor has published; absent until they publish. */
export interface PublishedContractorProfile {
	slug: string | null;
	companyName: string;
	companyDescription: string | null;
	teamMembers: Array<{
		id: string;
		name: string;
		role: string;
		experienceYears: number | null;
		shortBio: string | null;
		profilePhotoUrl: string | null;
	}>;
	projects: Array<{
		id: string;
		title: string;
		shortDescription: string;
		thumbnailUrl: string;
	}>;
}

export interface ContractorProfile {
	id: string;
	userId: string;
	name: string;
	businessName?: string | null;
	profilePictureUrl?: string;
	trades?: string[];
	experienceYears?: number;
	city?: string | null;
	state?: string | null;
	pincode?: string | null;
	serviceAreas?: string[];
	serviceRadiusKm?: number | null;
	/** CONTRACTOR_PROJECT_TYPES slugs. */
	projectTypes?: string[];
	projectTypesOther?: string | null;
	priceRangeMin?: number | null;
	priceRangeMax?: number | null;
	workPhotos?: string[];
	previousProjects?: PreviousProject[];
	bio?: string;
	isVerified: boolean;
	/** Present on `GET /contractors` list items only; null when unreviewed. */
	averageRating?: number | null;
	reviewCount?: number;
	reviewStatus?: "pending" | "approved" | "rejected";
	reviewNotes?: string | null;
	/** Present on the detail and project-team endpoints; null until the contractor publishes. */
	publishedProfile?: PublishedContractorProfile | null;
}

export interface ProjectContractorItem {
	id: string;
	projectId: string;
	contractorId: string;
	invitedBy?: string;
	status:
		| "invited"
		| "interested"
		| "quote_submitted"
		| "hired"
		| "completion_requested"
		| "completed"
		| "removed"
		| "declined";
	quoteAmount?: number;
	quoteDetails?: string;
	message?: string;
	/**
	 * Who pays the contractor, fixed when they are hired and null before:
	 * `platform_paid` from the project's escrow when the homeowner hired them,
	 * `designer_paid` by the designer directly when the designer did.
	 */
	paymentArrangement?: "platform_paid" | "designer_paid" | null;
	hiredAt?: string;
	completedAt?: string;
	createdAt: string;
	updatedAt: string;
	contractor?: ContractorProfile;
	project?: Project;
}

/**
 * What the public project endpoints return. Owner, property, designer and
 * funding fields are not part of it.
 */
export interface PublicProjectSummary {
	id: string;
	title: string;
	scope: Project["scope"];
	scopeDetails: { rooms?: string[] };
	budgetMin: number | null;
	budgetMax: number | null;
	timelineWeeks: number | null;
	createdAt: string;
	homeownerName: string;
	designerName: string | null;
	projectCity: string | null;
	previewImages: string[];
	description: string | null;
}

// ============================================================================
// DESIGNER API
// ============================================================================

/**
 * The signed-in user's own designer profile, or `null` when they have not
 * created one yet.
 *
 * `GET /designers/me` answers 404 for a user with no profile
 * (`getMyProfileHandler` in designers.controller.ts), which is an answer rather
 * than a failure, so it becomes `null`. Every other error is rethrown, so a
 * caller can tell a broken request from an absent profile — the designer
 * dashboard shows a retry for the first and an em dash for the second.
 */
export async function fetchDesignerProfile(): Promise<DesignerProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/designers/me`);
		return response.data.data.profile;
	} catch (error: unknown) {
		const err = error as { response?: { status?: number } };
		if (err.response?.status === 404) return null;
		throw error;
	}
}

/**
 * `fetchDesignerProfile` with every failure flattened to `null`.
 *
 * Kept for the caller that reads the profile outside React Query and treats a
 * missing profile as "onboarding incomplete" (`features/dashboard/pages/dashboard.tsx`).
 * Prefer `fetchDesignerProfile` in a query, where the failure has somewhere to
 * go.
 */
export async function getDesignerProfile(): Promise<DesignerProfile | null> {
	try {
		return await fetchDesignerProfile();
	} catch {
		return null;
	}
}

export interface BrowseDesignersParams {
	query?: string;
	city?: string;
	styles?: string[];
	budgetMin?: number;
	budgetMax?: number;
	page?: number;
	limit?: number;
}

export interface BrowseDesignersResult {
	designers: DesignerProfile[];
	total: number;
	page: number;
	totalPages: number;
	/** Present, and `true`, only when the ratings could not be read for this page. */
	ratingsUnavailable?: true;
}

/**
 * `browseDesigners` without the catch: a failed request throws instead of
 * flattening to an empty list, so a caller can show a retry rather than
 * "no designers". Same split as fetchDesignerProfile / getDesignerProfile.
 */
export async function fetchDesigners(
	params?: BrowseDesignersParams,
): Promise<BrowseDesignersResult> {
	const response = await apiClient.get(`${API_BASE}/designers`, { params });
	return response.data.data;
}

export async function browseDesigners(
	params?: BrowseDesignersParams,
): Promise<BrowseDesignersResult> {
	try {
		return await fetchDesigners(params);
	} catch {
		return { designers: [], total: 0, page: 1, totalPages: 0 };
	}
}

export async function getFeaturedDesigners(
	city?: string,
): Promise<DesignerProfile[]> {
	try {
		const response = await apiClient.get(`${API_BASE}/designers/featured`, {
			params: city ? { city } : undefined,
		});
		return response.data.data.designers;
	} catch {
		return [];
	}
}

export async function getDesignerById(
	id: string,
): Promise<DesignerProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/designers/${id}`);
		return response.data.data.designer;
	} catch {
		return null;
	}
}

export async function searchDesigners(params: {
	query?: string;
	city?: string;
	styles?: string[];
	budgetMin?: number;
	budgetMax?: number;
	page?: number;
	perPage?: number;
}): Promise<{
	designers: DesignerProfile[];
	total: number;
	page: number;
	totalPages: number;
}> {
	try {
		const response = await apiClient.get(`${API_BASE}/search/designers`, {
			params,
		});
		return response.data.data;
	} catch {
		return { designers: [], total: 0, page: 1, totalPages: 0 };
	}
}

// ============================================================================
// CONTRACTOR API
// ============================================================================

/**
 * The contractor twin of `fetchDesignerProfile`: `GET /contractors/me` answers
 * 404 for a user with no profile, which becomes `null`, and every other error
 * is rethrown. The contractor home needs the distinction: flattened, a failed
 * request reads as "no profile", and the page would offer an onboarded
 * contractor the onboarding form, where submitting can only answer 409.
 */
export async function fetchContractorProfile(): Promise<ContractorProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/contractors/me`);
		return response.data.data.profile;
	} catch (error: unknown) {
		const err = error as { response?: { status?: number } };
		if (err.response?.status === 404) return null;
		throw error;
	}
}

/**
 * `fetchContractorProfile` with every failure flattened to `null`. Kept for the
 * settings profile query (`features/settings/hooks/use-profile.ts`); prefer
 * `fetchContractorProfile` wherever a failure has somewhere to go.
 */
export async function getContractorProfile(): Promise<ContractorProfile | null> {
	try {
		return await fetchContractorProfile();
	} catch {
		return null;
	}
}

export interface BrowseContractorsResult {
	contractors: ContractorProfile[];
	total: number;
	page: number;
	totalPages: number;
	/** Present, and `true`, only when the ratings could not be read for this page. */
	ratingsUnavailable?: true;
}

interface BrowseContractorsParams {
	query?: string;
	trades?: string[];
	city?: string;
	page?: number;
	limit?: number;
}

/**
 * Same listing as `browseContractors`, but a failed request rejects. Use it
 * where the screen must tell "the request failed" apart from "no contractors
 * match" and offer a retry for the first.
 */
export async function fetchContractors(
	params?: BrowseContractorsParams,
): Promise<BrowseContractorsResult> {
	const response = await apiClient.get(`${API_BASE}/contractors`, { params });
	return response.data.data;
}

export async function browseContractors(
	params?: BrowseContractorsParams,
): Promise<BrowseContractorsResult> {
	try {
		return await fetchContractors(params);
	} catch {
		return { contractors: [], total: 0, page: 1, totalPages: 0 };
	}
}

export async function getContractorById(
	id: string,
): Promise<ContractorProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/contractors/${id}`);
		return response.data.data.contractor;
	} catch (error: unknown) {
		// Only a 404 means the contractor does not exist; any other failure
		// rejects so the page can offer a retry instead of "not found".
		const status = (error as { response?: { status?: number } }).response
			?.status;
		if (status === 404) return null;
		throw error;
	}
}

export async function searchContractors(params: {
	query?: string;
	trades?: string[];
	city?: string;
	page?: number;
	perPage?: number;
}): Promise<{
	contractors: ContractorProfile[];
	total: number;
	page: number;
	totalPages: number;
}> {
	try {
		const response = await apiClient.get(`${API_BASE}/search/contractors`, {
			params,
		});
		return response.data.data;
	} catch {
		return { contractors: [], total: 0, page: 1, totalPages: 0 };
	}
}

// ============================================================================
// PUBLIC PROJECTS & INTEREST API
// ============================================================================

export interface BrowsePublicProjectsParams {
	scope?: "full_home" | "partial";
	budgetMin?: number;
	budgetMax?: number;
	/** Matches a project's title, city, designer name or notes, server-side. */
	q?: string;
	/** Keeps projects whose rooms list contains this exact room name. */
	room?: string;
	page?: number;
	limit?: number;
}

export interface BrowsePublicProjectsResult {
	projects: PublicProjectSummary[];
	total: number;
	page: number;
	totalPages: number;
	facets: {
		/** Room names of the projects every filter but `room` leaves. */
		rooms: string[];
	};
}

/**
 * `browsePublicProjects` without the catch: a failed request throws instead
 * of flattening to an empty list, so a caller can show a retry rather than
 * "no projects". Same split as fetchDesignerProfile / getDesignerProfile.
 */
export async function fetchPublicProjects(
	params?: BrowsePublicProjectsParams,
): Promise<BrowsePublicProjectsResult> {
	const response = await apiClient.get(`${API_BASE}/projects/public`, {
		params,
	});
	return response.data.data;
}

export async function browsePublicProjects(
	params?: BrowsePublicProjectsParams,
): Promise<BrowsePublicProjectsResult> {
	try {
		return await fetchPublicProjects(params);
	} catch {
		return {
			projects: [],
			total: 0,
			page: 1,
			totalPages: 0,
			facets: { rooms: [] },
		};
	}
}

export async function getPublicProjectPreview(
	projectId: string,
): Promise<PublicProjectSummary | null> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/public/${projectId}`,
		);
		return response.data.data.project;
	} catch {
		return null;
	}
}

export interface SavedIdeas {
	projectIds: string[];
	projects: PublicProjectSummary[];
}

/**
 * The homeowner's saved projects. Throws on failure, unlike `getSavedIdeas`,
 * so a caller can tell a broken request from an empty list.
 */
export async function fetchSavedIdeas(): Promise<SavedIdeas> {
	const response = await apiClient.get(`${API_BASE}/projects/saved-ideas`);
	return response.data.data;
}

/**
 * Saves or unsaves a project and returns the ids now saved. Throws on
 * failure, so that a caller can keep the saved state it shows.
 */
export async function fetchToggleSavedIdea(
	projectId: string,
): Promise<{ saved: boolean; projectIds: string[] }> {
	const response = await apiClient.post(
		`${API_BASE}/projects/saved-ideas/${projectId}/toggle`,
	);
	return response.data.data;
}

export async function getSavedIdeas(): Promise<{
	projectIds: string[];
	projects: PublicProjectSummary[];
}> {
	try {
		const response = await apiClient.get(`${API_BASE}/projects/saved-ideas`);
		return response.data.data;
	} catch {
		return { projectIds: [], projects: [] };
	}
}

// Helper for post requests to avoid code duplication and return payload
async function handlePostRequest<T>(
	url: string,
	data?: unknown,
	defaultErrorMessage = "Operation failed",
): Promise<{ success: boolean; data?: T; error?: string }> {
	try {
		const response = await apiClient.post(url, data);
		return { success: true, data: response.data.data };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || defaultErrorMessage,
		};
	}
}

export async function expressInterest(data: {
	projectId: string;
	quoteAmount: number;
	quoteDetails?: string;
	message?: string;
}): Promise<{ success: boolean; data?: unknown; error?: string }> {
	return handlePostRequest(
		`${API_BASE}/contractors/me/interest`,
		data,
		"Failed to express interest",
	);
}

// ============================================================================
// CONTRACTOR INVITATIONS & ASSIGNMENTS API
// ============================================================================

export async function getContractorAssignments(): Promise<
	ProjectContractorItem[]
> {
	const response = await apiClient.get(
		`${API_BASE}/contractors/me/assignments`,
	);
	return response.data.data.assignments;
}

export async function getContractorInvitations(
	status?: string,
): Promise<ProjectContractorItem[]> {
	const response = await apiClient.get(
		`${API_BASE}/contractors/me/invitations`,
		{
			params: status ? { status } : undefined,
		},
	);
	return response.data.data.invitations;
}

export async function getInvitationDetails(
	invitationId: string,
): Promise<ProjectContractorItem | null> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/contractors/me/invitations/${invitationId}`,
		);
		return response.data.data.invitation;
	} catch (error: unknown) {
		const status = (error as { response?: { status?: number } }).response
			?.status;
		if (status === 404) return null;
		throw error;
	}
}

export async function submitContractorQuote(
	invitationId: string,
	data: { quoteAmount: number; quoteDetails?: string },
): Promise<{ success: boolean; data?: unknown; error?: string }> {
	return handlePostRequest(
		`${API_BASE}/contractors/me/invitations/${invitationId}/quote`,
		data,
		"Failed to submit quote",
	);
}

export async function declineInvitation(
	invitationId: string,
): Promise<{ success: boolean; data?: unknown; error?: string }> {
	return handlePostRequest(
		`${API_BASE}/contractors/me/invitations/${invitationId}/decline`,
		undefined,
		"Failed to decline invitation",
	);
}

export async function requestCompletion(
	assignmentId: string,
): Promise<{ success: boolean; data?: unknown; error?: string }> {
	return handlePostRequest(
		`${API_BASE}/contractors/me/assignments/${assignmentId}/request-completion`,
		undefined,
		"Failed to request completion",
	);
}

export async function confirmCompletion(
	assignmentId: string,
): Promise<{ success: boolean; data?: unknown; error?: string }> {
	return handlePostRequest(
		`${API_BASE}/project-contractors/${assignmentId}/confirm-completion`,
		undefined,
		"Failed to confirm completion",
	);
}

/**
 * `shortfall` is set only on the backend's `ESCROW_SHORTFALL` refusal: the
 * rupees the project's escrow has to gain before this payout can be made
 * without drawing on the designer's milestone money. The homeowner's Team tab
 * pre-fills its add-funds form with it, as it does for a refused hire.
 */
export async function authorizeAssignmentPayment(
	assignmentId: string,
): Promise<{
	success: boolean;
	data?: unknown;
	error?: string;
	shortfall?: number;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/project-contractors/${assignmentId}/authorize-payment`,
		);
		return { success: true, data: response.data.data };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		const shortfall = escrowShortfallOf(error);
		return {
			success: false,
			error: err.response?.data?.message || "Failed to authorize payment",
			...(shortfall !== undefined ? { shortfall } : {}),
		};
	}
}

export async function disputeCompletion(
	assignmentId: string,
): Promise<{ success: boolean; data?: unknown; error?: string }> {
	return handlePostRequest(
		`${API_BASE}/project-contractors/${assignmentId}/dispute`,
		undefined,
		"Failed to dispute completion",
	);
}
